import {
	DispatcherV2Response,
	GroupData,
	GroupMode,
	GroupType,
	ItemEntityType,
	ItemsByGroup,
	RowMetaData,
	SortingRequest
} from '@bringg/types';
import { action, computed, makeObservable, observable } from 'mobx';

import { TableDefinitionFilters, TableItem } from './types';
import { getInnerGroups, getLastGroup, isOneLevelGroup, isTwoLevelsGroup } from '../../utils/grouping';
import {
	RunFiltersObject,
	TaskFiltersObject
} from 'bringg-web/features/monitoring/shared/components/monitoring-filter-bar/types';

const PAGE_SIZE = 150;

export class TableDefinition {
	public readonly groupMode: GroupMode;
	public readonly filters: TableDefinitionFilters;
	public readonly sorting: SortingRequest;
	public readonly itemsByGroup: ItemsByGroup;
	public readonly itemToGroupId: Map<number, string> = new Map();
	public groups: GroupData[];
	public collapsedGroups: Record<string, boolean>;
	public groupsMap: Record<string, GroupData>;
	public entityBaseType: ItemEntityType;
	public hasMoreData: boolean;
	public isCollapseAll: boolean;
	public totalTasks: number;

	constructor(
		filters: {
			task?: TaskFiltersObject;
			run?: RunFiltersObject;
		},
		sorting: SortingRequest,
		groupMode: GroupMode,
		entityBaseType: ItemEntityType = 'task'
	) {
		this.filters = filters;
		this.sorting = sorting;
		this.groupMode = groupMode;
		this.collapsedGroups = {};
		this.groups = [];
		this.groupsMap = {};
		this.itemsByGroup = {};
		this.hasMoreData = true;
		this.entityBaseType = entityBaseType;

		makeObservable(this, {
			total: computed,
			allTasksIds: computed,
			flatTableItems: computed,
			addGroup: action,
			removeGroup: action,
			setResponse: action,
			setGroupsResponse: action,
			toggleCollapseAll: action,
			toggleGroupCollapse: action,
			setItemToGroupId: action,
			groups: observable,
			groupMode: observable,
			hasMoreData: observable,
			itemsByGroup: observable,
			isCollapseAll: observable,
			collapsedGroups: observable,
			itemToGroupId: observable,
			setHasMoreData: action
		});
	}

	public get entityType(): ItemEntityType {
		if (!this.isCollapseAll) {
			return this.entityBaseType;
		}

		if (isTwoLevelsGroup(this.groupMode)) {
			return 'top_level_group';
		}

		if (isOneLevelGroup(this.groupMode)) {
			return 'task_group';
		}
	}

	public get total(): number {
		return this.flatTableItems.length + (this.hasMoreData ? 1 : 0);
	}

	public get flatTableItems(): TableItem[] {
		const flatTableItems: TableItem[] = [];
		this.groups.forEach(group => this.addItemToFlatList(group, flatTableItems));
		return flatTableItems;
	}

	get allTasksIds(): number[] {
		return Array.from(this.itemToGroupId.keys());
	}

	private addItemToFlatList(group: GroupData, flatTableItems: TableItem[]) {
		if (!this.itemsByGroup[group.id] && !group.groups?.length) {
			return;
		}

		if (group.id !== GroupMode.None) {
			flatTableItems.push(group);
		}

		if (this.isCollapsed(group.id)) {
			return;
		}

		group.groups?.forEach(childItem => this.addItemToFlatList(childItem, flatTableItems));

		this.itemsByGroup[group.id]?.forEach(childItem => {
			flatTableItems.push(childItem);
			this.setItemToGroupId(childItem.id, group.id);
		});
	}

	public setItemToGroupId(id: number, groupId: string) {
		this.itemToGroupId.set(id, groupId);
	}

	public toggleGroupCollapse(groupId: string, isCollapsed: boolean) {
		this.collapsedGroups[groupId || null] = isCollapsed;
		this.isCollapseAll = false;
	}

	public isCollapsed(groupId: string) {
		return this.isCollapseAll || this.collapsedGroups[groupId];
	}

	public toggleCollapseAll(isCollapsed: boolean) {
		this.toggleCollapseAllRecursive(this.groups, isCollapsed);
		this.isCollapseAll = isCollapsed;
	}

	private toggleCollapseAllRecursive(groups: GroupData[], isCollapsed: boolean) {
		for (const group of groups) {
			this.toggleGroupCollapse(group.id, isCollapsed);

			if (group.groups) {
				this.toggleCollapseAllRecursive(group.groups, isCollapsed);
			}
		}
	}

	public setResponse(response: DispatcherV2Response, isLastGroupFull: boolean) {
		const taskResponse = response.query_responses[0];
		const topLevelGroups = response.query_responses[1]?.groups;

		this.setGroups(taskResponse.groups);
		this.setItemsByGroup(taskResponse.items_by_group);

		this.totalTasks = taskResponse.total;
		this.setHasMoreData(response);

		if (taskResponse.groups.length > 0) {
			this.setTasksCounts(
				taskResponse.groups,
				topLevelGroups,
				taskResponse.total,
				this.hasMoreData && !isLastGroupFull ? getLastGroup(taskResponse.groups).id : undefined
			);
		}
	}

	public setGroupsResponse(response: DispatcherV2Response, loadedGroupId: string) {
		const groupResponse = response.query_responses[0];

		if (groupResponse.groups.length === 0) {
			if (this.itemsByGroup[loadedGroupId]) {
				this.groupsMap[loadedGroupId].data.task_count = groupResponse.total;
			} else {
				this.setLastGroupTasksCount(loadedGroupId);
			}

			return;
		}

		this.setGroups(groupResponse.groups);
		this.setItemsByGroup(groupResponse.items_by_group);
		this.setHasMoreData();

		if (this.itemsByGroup[loadedGroupId]) {
			this.groupsMap[loadedGroupId].data.task_count = groupResponse.total;
		} else {
			this.setTasksCounts(
				groupResponse.groups,
				undefined,
				groupResponse.total,
				this.hasMoreData ? getLastGroup(groupResponse.groups).id : undefined
			);
		}
	}

	public isGroupFullyLoaded(group: GroupData) {
		if (!this.hasMoreData) {
			return true;
		}

		if (!group) {
			return false;
		}

		if (this.itemsByGroup[group.id]) {
			return group.data.task_count === this.itemsByGroup[group.id].length;
		}

		return (
			group.data.task_count ===
			group.groups.reduce((total, nestedGroup) => total + nestedGroup.data.task_count, 0)
		);
	}

	public isGroupPartiallyLoaded(group: GroupData) {
		if (!this.hasMoreData || group.data.task_count === 0) {
			return true;
		}

		if (this.itemsByGroup[group.id]) {
			return this.itemsByGroup[group.id].length > 0;
		}

		return group.groups.some(nestedGroup => this.isGroupPartiallyLoaded(nestedGroup));
	}

	private setGroups(groups: GroupData[]) {
		if (groups?.length) {
			this.addGroups(this.groups, groups, true);
		} else if (this.groups.length === 0 && this.groupMode === GroupMode.None) {
			this.addGroup(this.groups, { groupId: GroupMode.None });
		}

		if (this.isCollapseAll) {
			this.toggleCollapseAll(true);
		}
	}

	public addGroup(
		parent: GroupData[],
		params: { groupId: string; groupType?: GroupType; data?: RowMetaData; index?: number }
	) {
		const observableGroup: GroupData = observable({
			id: params.groupId,
			group_type: params.groupType,
			data: params.data
		});

		if (params.index === undefined) {
			parent.push(observableGroup);
		} else {
			parent.splice(params.index, 0, observableGroup);
		}

		this.groupsMap[params.groupId] = observableGroup;
	}

	public removeGroup(parent: GroupData[], index: number) {
		const groupToDelete = parent[index];
		parent.splice(index, 1);
		delete this.groupsMap[groupToDelete.id];
		delete this.itemsByGroup[groupToDelete.id];
	}

	private setItemsByGroup(itemsByGroup: ItemsByGroup) {
		const groupIds = Object.keys(itemsByGroup);

		for (const groupId of groupIds) {
			if (this.itemsByGroup[groupId]) {
				const itemsById = this.itemsByGroup[groupId].reduce((byId, item) => {
					byId[item.id] = item;
					return byId;
				}, {});

				this.itemsByGroup[groupId] = this.itemsByGroup[groupId].concat(
					itemsByGroup[groupId].filter(item => !itemsById[item.id])
				);
			} else {
				this.itemsByGroup[groupId] = itemsByGroup[groupId];
			}
		}
	}

	private addGroups(parentGroups: GroupData[], responseGroups: GroupData[], addToParent: boolean) {
		if (!responseGroups) {
			return;
		}

		for (let i = 0; i < responseGroups.length; i++) {
			const group = responseGroups[i];

			if (!this.groupsMap[group.id]) {
				const observableGroup = observable(group);
				this.groupsMap[group.id] = observableGroup;

				if (addToParent) {
					parentGroups.push(observableGroup);
				} else {
					parentGroups[i] = observableGroup;
				}

				this.addGroups(parentGroups[parentGroups.length - 1].groups, group.groups, false);
			} else {
				this.addGroups(this.groupsMap[group.id].groups, group.groups, true);
			}
		}
	}

	private setTasksCounts(
		responseGroups: GroupData[],
		topLevelGroups: GroupData[],
		totalTasks: number,
		lastGroupId: string
	) {
		let i = 0;

		if (this.groupMode === GroupMode.None) {
			this.groupsMap[responseGroups[0].id].data.task_count = totalTasks;
			return;
		}

		for (const group of responseGroups || []) {
			const storedGroup = this.groupsMap[group.id];

			if (group.id === lastGroupId && !topLevelGroups) {
				return;
			}

			if (this.itemsByGroup[storedGroup.id]) {
				if (storedGroup.data.task_count === undefined) {
					if (topLevelGroups) {
						storedGroup.data.task_count = topLevelGroups[i].data.task_count;
					} else {
						storedGroup.data.task_count = this.itemsByGroup[storedGroup.id].length;
					}
				}
			} else {
				if (topLevelGroups) {
					storedGroup.data.task_count = topLevelGroups[i].data.task_count;
				}

				this.setTasksCounts(storedGroup.groups, undefined, totalTasks, lastGroupId);
			}

			i++;
		}
	}

	private setLastGroupTasksCount(loadedGroupId: string) {
		const lastGroupId = getLastGroup([this.groupsMap[loadedGroupId]]).id;
		this.groupsMap[lastGroupId].data.task_count = this.itemsByGroup[lastGroupId].length;
	}

	public isLoaded(flatArrayIndex: number): boolean {
		return !!this.flatTableItems[flatArrayIndex];
	}

	public setHasMoreData(response?: DispatcherV2Response) {
		if (this.entityType === 'task') {
			const totalReturnedTasks = Object.values(this.itemsByGroup).reduce((total, items) => {
				return total + items.length;
			}, 0);

			this.hasMoreData = this.totalTasks > totalReturnedTasks;
		} else {
			const groupsFromResponse = response ? response.query_responses[0].groups : this.groups;
			const groups = getInnerGroups(groupsFromResponse);
			this.hasMoreData = groups.length === PAGE_SIZE; //todo - take it from response total https://bringg.atlassian.net/browse/BRNGG-31274
		}
	}
}
